import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, Ban, Loader2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUserStore } from "@/store/userStore";
import { useTranslation } from "react-i18next";
import {
  sessionDebtsApi,
  type SessionDebtItem,
} from "@/services/sessionDebtsApi";

function fmtDate(d?: string | null) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("fr-FR");
  } catch {
    return d;
  }
}

export default function SessionDebts() {
  const { t } = useTranslation(["adminui3", "common"]);
  const navigate = useNavigate();
  const gymId = useUserStore((s) => s.gymId);
  const [items, setItems] = useState<SessionDebtItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<SessionDebtItem | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    if (!gymId) return;
    setLoading(true);
    try {
      const res = await sessionDebtsApi.list(gymId);
      setItems(res.debts || []);
    } catch (e: any) {
      toast.error(e?.message || t("sessionDebts.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymId]);

  const totals = useMemo(() => {
    const sessions = items.reduce((s, d) => s + (d.debt_sessions || 0), 0);
    return { clients: items.length, sessions };
  }, [items]);

  const handleScanAll = async () => {
    if (!gymId) return;
    setScanning(true);
    try {
      const res = await sessionDebtsApi.scanAll(gymId);
      toast.success(
        t("sessionDebts.scanSuccess", { created: res.created, updated: res.updated, cleared: res.cleared })
      );
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("sessionDebts.scanError"));
    } finally {
      setScanning(false);
    }
  };

  const handleRecomputeOne = async (item: SessionDebtItem) => {
    if (!gymId) return;
    setBusyId(item.id);
    try {
      await sessionDebtsApi.recomputeUser(gymId, item.user_id);
      toast.success(t("sessionDebts.recomputeSuccess"));
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("sessionDebts.genericError"));
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async () => {
    if (!gymId || !cancelTarget) return;
    setBusyId(cancelTarget.id);
    try {
      await sessionDebtsApi.cancel(gymId, cancelTarget.id);
      toast.success(t("sessionDebts.cancelSuccess"));
      setCancelTarget(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("sessionDebts.genericError"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/clients")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {t("sessionDebts.backToClients")}
        </Button>
        <h1 className="text-xl md:text-2xl font-semibold">{t("sessionDebts.title")}</h1>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            {t("sessionDebts.refresh")}
          </Button>
          <Button size="sm" onClick={handleScanAll} disabled={scanning || !gymId}>
            {scanning ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            {t("sessionDebts.recalculateAll")}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {t("sessionDebts.summary", { clients: totals.clients, sessions: totals.sessions })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-3">
            {t("sessionDebts.explanation")}
          </p>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("sessionDebts.columnClient")}</TableHead>
                  <TableHead>{t("sessionDebts.columnDebt")}</TableHead>
                  <TableHead>{t("sessionDebts.columnSourceSubscription")}</TableHead>
                  <TableHead>{t("sessionDebts.columnPeriod")}</TableHead>
                  <TableHead>{t("sessionDebts.columnComputedAt")}</TableHead>
                  <TableHead className="text-right">{t("sessionDebts.columnActions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      {t("sessionDebts.loading")}
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      {t("sessionDebts.noDebts")}
                    </TableCell>
                  </TableRow>
                )}
                {items.map((d) => {
                  const name = [d.client?.first_name, d.client?.last_name]
                    .filter(Boolean)
                    .join(" ") || t("sessionDebts.unknownClient");
                  return (
                    <TableRow key={d.id}>
                      <TableCell>
                        <button
                          className="text-left hover:underline"
                          onClick={() => navigate(`/admin/clients/${d.user_id}`)}
                        >
                          {name}
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge variant="destructive">−{d.debt_sessions}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {d.source_subscription?.type ?? "—"}
                        {d.source_subscription?.sessions_total != null && (
                          <span className="text-muted-foreground">
                            {" "}
                            {t("sessionDebts.sessionsCount", { count: d.source_subscription.sessions_total })}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {fmtDate(d.source_subscription?.start_date)} →{" "}
                        {fmtDate(d.source_subscription?.end_date)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {fmtDate(d.computed_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busyId === d.id}
                            onClick={() => handleRecomputeOne(d)}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            {t("sessionDebts.recalculate")}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyId === d.id}
                            onClick={() => setCancelTarget(d)}
                          >
                            <Ban className="h-3 w-3 mr-1" />
                            {t("sessionDebts.cancel")}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("sessionDebts.cancelDialogTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("sessionDebts.cancelDialogDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("sessionDebts.back")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel}>{t("sessionDebts.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
